import { Copy, Link, Loader2, Lock, Plus, Share2, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { createShareToken, listShareTokens, revokeShareToken } from '@/services/reviewService';

import type { ShareScope, ShareToken } from '@/types/submission.types';

interface ShareSectionProps {
  applicationId: string;
}

const SCOPE_OPTIONS: ShareScope[] = ['full', 'docs', 'video', 'application'];

const EXPIRY_OPTIONS = [24, 72, 168, 720] as const;

const SCOPE_BADGE_CLASSES: Record<ShareScope, string> = {
  full: 'bg-blue-100 text-blue-700',
  docs: 'bg-green-100 text-green-700',
  video: 'bg-purple-100 text-purple-700',
  application: 'bg-gray-100 text-gray-700',
};

function isExpired(expiresAt: string): boolean {
  return new Date(expiresAt) < new Date();
}

function isRevoked(token: ShareToken): boolean {
  return !!token.revokedAt;
}

export function ShareSection({ applicationId }: ShareSectionProps) {
  const { t } = useTranslation();
  const [shares, setShares] = useState<ShareToken[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Form state
  const [scope, setScope] = useState<ShareScope>('full');
  const [expiresInHours, setExpiresInHours] = useState(72);
  const [allowedDomain, setAllowedDomain] = useState('');
  const [password, setPassword] = useState('');

  const loadShares = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await listShareTokens(applicationId);
      setShares(result);
    } finally {
      setIsLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    void loadShares();
  }, [loadShares]);

  const handleStartCreate = useCallback(() => {
    setScope('full');
    setExpiresInHours(72);
    setAllowedDomain('');
    setPassword('');
    setIsCreating(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsCreating(false);
  }, []);

  const handleCreate = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const result = await createShareToken({
        applicationId,
        scope,
        expiresInHours,
        allowedDomain: allowedDomain || undefined,
        password: password || undefined,
      });
      setShares((prev) => [result, ...prev]);
      setIsCreating(false);
      toast.success(t('submissionDetail.overview.shareCreated'));
    } catch {
      toast.error(t('submissionDetail.overview.shareError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [applicationId, scope, expiresInHours, allowedDomain, password, t]);

  const handleCopy = useCallback(
    async (token: ShareToken) => {
      const url = `${window.location.origin}/shared/${token.token}`;
      try {
        await navigator.clipboard.writeText(url);
        setCopiedId(token.shareTokenId);
        toast.success(t('submissionDetail.overview.copied'));
        setTimeout(() => {
          setCopiedId(null);
        }, 2000);
      } catch {
        // Clipboard API may fail in some contexts
      }
    },
    [t],
  );

  const handleRevoke = useCallback(
    async (shareTokenId: string) => {
      setRevokingId(shareTokenId);
      try {
        const result = await revokeShareToken(shareTokenId);
        setShares((prev) => prev.map((s) => (s.shareTokenId === shareTokenId ? result : s)));
        toast.success(t('submissionDetail.overview.shareRevoked'));
      } catch {
        toast.error(t('submissionDetail.overview.revokeError'));
      } finally {
        setRevokingId(null);
      }
    },
    [t],
  );

  const getScopeLabel = useCallback(
    (s: ShareScope): string => {
      const labels: Record<ShareScope, string> = {
        full: t('submissionDetail.overview.shareScopeFull'),
        docs: t('submissionDetail.overview.shareScopeDocs'),
        video: t('submissionDetail.overview.shareScopeVideo'),
        application: t('submissionDetail.overview.shareScopeApplication'),
      };
      return labels[s];
    },
    [t],
  );

  const getExpiryLabel = useCallback(
    (hours: number): string => {
      const key = `submissionDetail.overview.shareExpiry${String(hours)}`;
      return t(key);
    },
    [t],
  );

  const isPasswordValid = password.length === 0 || password.length >= 4;

  return (
    <div className='border-t border-gray-200 pt-4 mt-4'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-sm font-semibold text-gray-700 flex items-center gap-1.5'>
          <Share2 size={16} />
          {t('submissionDetail.overview.sharing')}
        </h3>
        {!isCreating ? (
          <button
            type='button'
            onClick={handleStartCreate}
            className='text-xs text-blue-600 hover:text-blue-800 font-medium'>
            {t('submissionDetail.overview.generateShare')}
          </button>
        ) : null}
      </div>

      {/* Create form */}
      {isCreating ? (
        <div className='space-y-3 mb-4'>
          <div>
            <label htmlFor='share-scope' className='block text-xs text-gray-500 mb-1'>
              {t('submissionDetail.overview.shareScope')}
            </label>
            <select
              id='share-scope'
              value={scope}
              onChange={(e) => setScope(e.target.value as ShareScope)}
              className={cn(
                'w-full rounded-md border border-gray-300 px-3 py-2 text-sm',
                'focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none',
              )}>
              {SCOPE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {getScopeLabel(s)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor='share-expiry' className='block text-xs text-gray-500 mb-1'>
              {t('submissionDetail.overview.shareExpiry')}
            </label>
            <select
              id='share-expiry'
              value={expiresInHours}
              onChange={(e) => setExpiresInHours(Number(e.target.value))}
              className={cn(
                'w-full rounded-md border border-gray-300 px-3 py-2 text-sm',
                'focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none',
              )}>
              {EXPIRY_OPTIONS.map((hours) => (
                <option key={hours} value={hours}>
                  {getExpiryLabel(hours)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor='share-domain' className='block text-xs text-gray-500 mb-1'>
              {t('submissionDetail.overview.shareDomain')}
            </label>
            <input
              id='share-domain'
              type='text'
              value={allowedDomain}
              onChange={(e) => setAllowedDomain(e.target.value)}
              placeholder={t('submissionDetail.overview.shareDomainPlaceholder')}
              className={cn(
                'w-full rounded-md border border-gray-300 px-3 py-2 text-sm',
                'focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none',
              )}
            />
          </div>

          <div>
            <label htmlFor='share-password' className='block text-xs text-gray-500 mb-1'>
              {t('submissionDetail.overview.sharePassword')}
            </label>
            <input
              id='share-password'
              type='text'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('submissionDetail.overview.sharePasswordPlaceholder')}
              className={cn(
                'w-full rounded-md border border-gray-300 px-3 py-2 text-sm',
                'focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none',
              )}
            />
          </div>

          <div className='flex gap-2'>
            <button
              type='button'
              onClick={() => void handleCreate()}
              disabled={isSubmitting || !isPasswordValid}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-white',
                'bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed',
              )}>
              {isSubmitting ? <Loader2 size={14} className='animate-spin' /> : <Plus size={14} />}
              {isSubmitting ? t('submissionDetail.overview.creating') : t('submissionDetail.overview.createShare')}
            </button>
            <button
              type='button'
              onClick={handleCancel}
              disabled={isSubmitting}
              className='rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100'>
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      ) : null}

      {/* Shares list */}
      {isLoading ? (
        <div className='flex items-center gap-2 text-sm text-gray-500'>
          <Loader2 size={14} className='animate-spin' />
          {t('common.loading')}
        </div>
      ) : shares.length === 0 ? (
        <p className='text-sm text-gray-400 italic'>{t('submissionDetail.overview.noShares')}</p>
      ) : (
        <div className='space-y-2'>
          {shares.map((token) => {
            const tokenExpired = isExpired(token.expiresAt);
            const tokenRevoked = isRevoked(token);
            const isDimmed = tokenRevoked || tokenExpired;

            return (
              <div
                key={token.shareTokenId}
                className={cn('rounded-md border border-gray-200 p-3 text-sm', isDimmed && 'opacity-50')}>
                <div className='flex items-center justify-between gap-2'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span
                      className={cn(
                        'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
                        SCOPE_BADGE_CLASSES[token.scope],
                      )}>
                      {getScopeLabel(token.scope)}
                    </span>
                    {tokenRevoked ? (
                      <span className='inline-block rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-medium'>
                        {t('submissionDetail.overview.revoked')}
                      </span>
                    ) : tokenExpired ? (
                      <span className='text-xs text-red-600 font-medium'>{t('submissionDetail.overview.expired')}</span>
                    ) : (
                      <span className='text-xs text-gray-500'>
                        {t('submissionDetail.overview.expiresAt')} {new Date(token.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                    <span className='text-xs text-gray-400'>
                      {t('submissionDetail.overview.accesses', { count: token.accessCount })}
                    </span>
                    {token.isPasswordProtected ? (
                      <span title={t('submissionDetail.overview.passwordProtected')}>
                        <Lock size={12} className='text-gray-400' />
                      </span>
                    ) : null}
                  </div>
                  <div className='flex items-center gap-1'>
                    {!isDimmed ? (
                      <>
                        <button
                          type='button'
                          onClick={() => void handleCopy(token)}
                          title={t('submissionDetail.overview.copyLink')}
                          className='rounded p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50'>
                          {copiedId === token.shareTokenId ? <Copy size={14} /> : <Link size={14} />}
                        </button>
                        <button
                          type='button'
                          onClick={() => void handleRevoke(token.shareTokenId)}
                          disabled={revokingId === token.shareTokenId}
                          title={t('submissionDetail.overview.revoke')}
                          className='rounded p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50'>
                          {revokingId === token.shareTokenId ? (
                            <Loader2 size={14} className='animate-spin' />
                          ) : (
                            <Trash2 size={14} />
                          )}
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
